import { Selection, EnterElement, scaleBand, ScaleBand, select } from "d3"
import { D3TracesWrapper } from "./D3TracesWrapper"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { ModalityGraphConfig, ModalityGraphConfigWithDerivedTraces } from "../../../../Types/Graph"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { RenderStrategy, SpectrogramTraceConfig, SpectrogramTraceConfigJSON, TraceConfig } from "../../../../Types/Trace"
import { D3BandAxis } from "./D3BandAxis"
import { D3SpectrogramGraphLegend } from "./D3SpectrogramGraphLegend"
import { D3SpectrogramGridLines } from "./D3SpectrogramGridLines"

export class D3SpectrogramGraph extends D3OneToOneRenderable<SVGGElement, SVGGElement, ModalityGraphConfig, ModalityGraphGroupReactCallbacks> {
	private tracePositionScale: ScaleBand<string> = scaleBand()
	private pageManager: TimeSeriesPageManager<ModalityPage>
	private graph: ModalityGraphConfigWithDerivedTraces
	private foregroundClassName: string = "d3-spectrogram-graph-foreground"
	private backgroundClassName: string = "d3-spectrogram-graph-background"

	private tracesWrapper?: D3TracesWrapper
	private yAxes: Map<string, D3BandAxis> = new Map()
	private yGridLines: Map<string, D3SpectrogramGridLines> = new Map()
	private legend?: D3SpectrogramGraphLegend

	constructor(root: SVGGElement, config: ModalityGraphConfig, pageManager: TimeSeriesPageManager<ModalityPage>, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, "d3-modality-spectrogram-graph", reactCallbacks)
		this.pageManager = pageManager
		this.graph = {} as ModalityGraphConfigWithDerivedTraces

		this.updateDerivedState()
		this.render()
	}

	protected updateDerivedState(): void {
		const spectrogramTraces = this.config.traces.filter(
			trace => trace.renderStrategy === RenderStrategy.SPECTROGRAM
		) as SpectrogramTraceConfigJSON[]

		const visibleTraces = spectrogramTraces.filter(
			trace => this.config.hideEmptyGraphs ? (trace.isVisible ?? true) : true
		)

		this.tracePositionScale
			.domain(visibleTraces.map(trace => trace.id))
			.range([0, this.config.height])

		if (visibleTraces.length > 3) {
			this.tracePositionScale.paddingInner(0.3)
		} else if (visibleTraces.length > 1) {
			this.tracePositionScale.paddingInner(0.2)
		} else {
			this.tracePositionScale.paddingInner(0)
		}

		this.graph = this.getDerivedGraph() as ModalityGraphConfigWithDerivedTraces
		this.updateChildren()
	}

	public renderPage = (page: ModalityPage) => {
		this.tracesWrapper?.renderPage(page)
	}

	public viewTimesChanged = () => {
		this.tracesWrapper?.render()
	}

	public rescale = () => {
		this.yAxes.forEach(axis => axis.render())
		this.yGridLines.forEach(gridLines => gridLines.render())
		this.tracesWrapper?.rescale()
	}

	public takeSnapshot = () => {
		this.tracesWrapper?.takeSnapshot()
	}

	public clearSnapshot = () => {
		this.tracesWrapper?.clearSnapshot()
	}

	public isRescaling = (): boolean => {
		return this.tracesWrapper?.isRescaling() ?? false
	}

	public autoScale = () => {}

	public getDerivedGraph(): ModalityGraphConfigWithDerivedTraces {
		const spectrogramTraces = this.config.traces.filter(
			trace => trace.renderStrategy === RenderStrategy.SPECTROGRAM
		) as SpectrogramTraceConfigJSON[]

		const visibleTraces = spectrogramTraces.filter(
			trace => this.config.hideEmptyGraphs ? (trace.isVisible ?? true) : true
		)

		return {
			...this.config,
			traces: visibleTraces.map(traceConfig => {
				const traceYStart = this.tracePositionScale(traceConfig.id) ?? 0
				const traceBandHeight = this.tracePositionScale.bandwidth()
				const frequencyLabels = traceConfig.frequencyLabels ?? []

				const frequencyScale: ScaleBand<string> = scaleBand<string>()
					.domain([...frequencyLabels].reverse())
					.range([traceYStart, traceYStart + traceBandHeight])
					.paddingInner(0)
					.paddingOuter(0.5)

				return {
					...traceConfig,
					graphId: this.config.id,
					xScale: this.config.xScale,
					yScale: frequencyScale,
				} as SpectrogramTraceConfig
			}) as TraceConfig[],
		}
	}

	protected enter = (newGraph: Selection<EnterElement, any, any, any>): Selection<SVGGElement, any, any, any> => {
		const graphGroup = newGraph
			.append("g")
			.attr("class", this.className)
			.attr("transform", `translate(0, ${this.config.offset})`)

		graphGroup
			.append("g")
			.attr("class", this.backgroundClassName)
			.each(this.createBackgroundChildren)

		graphGroup
			.append("g")
			.attr("class", this.foregroundClassName)
			.each(this.createForegroundChildren)

		return graphGroup
	}

	protected update = (updatedGraphs: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		updatedGraphs.attr("transform", `translate(0, ${this.config.offset})`)
		this.renderChildren()
		return updatedGraphs
	}

	protected createForegroundChildren = (_config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]

		this.graph.traces.forEach(traceConfig => {
			const trace = traceConfig as SpectrogramTraceConfig
			const axisRoot = select(root).append("g").attr("class", "d3-band-axis-root").node()!
			const tickValues = this.getFrequencyTickValues(trace)

			const axis = new D3BandAxis(axisRoot, {
				scale: trace.yScale,
				tickValues,
				formatTick: this.formatFrequencyLabel,
			}, this.reactCallbacks)
			this.yAxes.set(trace.id, axis)
		})

		this.legend = new D3SpectrogramGraphLegend(
			root,
			{
				traces: this.graph.traces as SpectrogramTraceConfigJSON[],
				tracePositionScale: this.tracePositionScale,
				xOffset: this.config.width,
				graphId: this.config.id,
			},
			"d3-spectrogram-graph-legend",
			this.reactCallbacks
		)
	}

	protected createBackgroundChildren = (_config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]

		this.graph.traces.forEach(traceConfig => {
			const trace = traceConfig as SpectrogramTraceConfig
			const gridLinesRoot = select(root).append("g").node()!
			const gridLines = new D3SpectrogramGridLines(gridLinesRoot, {
				scale: trace.yScale,
				tickValues: this.getFrequencyTickValues(trace),
				width: this.config.width,
			})
			this.yGridLines.set(trace.id, gridLines)
		})

		this.tracesWrapper = new D3TracesWrapper(root, { traces: this.graph.traces }, this.pageManager, this.reactCallbacks)
	}

	protected updateChildren = () => {
		this.tracesWrapper?.updateConfig({ traces: this.graph.traces })

		this.graph.traces.forEach(traceConfig => {
			const trace = traceConfig as SpectrogramTraceConfig
			const tickValues = this.getFrequencyTickValues(trace)

			this.yAxes.get(trace.id)?.updateConfig({
				scale: trace.yScale,
				tickValues,
				formatTick: this.formatFrequencyLabel,
			})

			this.yGridLines.get(trace.id)?.updateConfig({
				scale: trace.yScale,
				tickValues,
				width: this.config.width,
			})
		})

		this.legend?.updateConfig({
			traces: this.graph.traces as SpectrogramTraceConfigJSON[],
			tracePositionScale: this.tracePositionScale,
			xOffset: this.config.width,
			graphId: this.config.id,
		})
	}

	private renderChildren = () => {
		this.tracesWrapper?.render()
		this.yAxes.forEach(axis => axis.render())
		this.yGridLines.forEach(gridLines => gridLines.render())
		this.legend?.render()
	}

	private getFrequencyTickValues(trace: SpectrogramTraceConfig): string[] {
		const domain = trace.yScale.domain()
		const numericDomain = domain.map(label => Number(label))
		const targetFrequencies = Array.from({ length: 5 }, (_, index) => index * 5)

		const ticks: string[] = []

		targetFrequencies.forEach(target => {
			let closestLabel: string | undefined
			let smallestDifference = Number.POSITIVE_INFINITY

			numericDomain.forEach((value, index) => {
				const difference = Math.abs(value - target)
				if (difference < smallestDifference) {
					smallestDifference = difference
					closestLabel = domain[index]
				}
			})

			if (closestLabel && !ticks.includes(closestLabel)) {
				ticks.push(closestLabel)
			}
		})

		return ticks
	}

	private formatFrequencyLabel = (value: string): string => {
		const numericValue = Number(value)
		if (Number.isNaN(numericValue)) {
			return value
		}

		return (Math.round(numericValue / 5) * 5).toString()
	}
}
